// src/pages/admin/AdminBulkUpload.js
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { createEducatorWithStudents } from "../../utils/createUserWithStudents";
import "./AdminBulkUpload.css"; // optional styling
// import { emailTemplates } from "../../templates/emailTemplates";
import { mergeTemplate } from "../../utils/mergeTemplate";
import { loadEmailTemplates } from "../../utils/loadEmailTemplates";
import { collection, getDocs, query, where } from "firebase/firestore";
import { sendEmailViaGmail } from "../../utils/sendEmailViaGmail";



const AdminBulkUpload = () => {
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const db = getFirestore();
  const [emailPreview, setEmailPreview] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [sentMap, setSentMap] = useState({});

  const [emailTemplates, setEmailTemplates] = useState([]);
const [loadingTemplates, setLoadingTemplates] = useState(true);
const [sendingBulk, setSendingBulk] = useState(false);
const [bulkProgress, setBulkProgress] = useState(0);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setRows([]);
    setResults([]);
    setProgress(0);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        setRows(json);
      } catch (err) {
        console.error(err);
        setError("Unable to read file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const selectedTemplate = emailTemplates.find(
    (t) => t.id === selectedTemplateId
  );  
  
  // Map a row from Excel into an educator object
  const mapRowToEducator = (row) => {
    return {
      sirName: row["Sir Name"] || "",
      firstName: row["First Name"] || "",
      lastName: row["Last Name"] || "",
      email: row["Email"] || "",
      password: row["Password"] || "", // optional override
      schoolName: row["School Name"] || "",
      schoolDistrict: row["School District"] || row["District"] || "",
      state: row["State"] || "",
      grade: row["Grade"] || "",
      view: row["View"] || "Educator",
      role: row["Role"] || "",
      notes: row["Notes"] || "",
      educatorCode: row["Educator Code"] || row["EducatorCode"] || "",
    };
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await loadEmailTemplates(db);
        setEmailTemplates(templates);
  
        if (templates.length) {
          setSelectedTemplateId(templates[0].id);
        }
      } catch (err) {
        console.error("Failed to load email templates", err);
      } finally {
        setLoadingTemplates(false);
      }
    };
  
    fetchTemplates();
  }, [db]);
  
  // Pull "Student1 First Name", "Student1 Last Initial", etc. from the row
  const extractStudentsFromRow = (row) => {
    const byIndex = {};
    for (const [key, value] of Object.entries(row)) {
      const m = key.match(/^Student\s*(\d+)\s*(First Name|Last Initial)$/i);
      if (!m) continue;
      const idx = m[1];
      const field = m[2].toLowerCase().includes("first") ? "firstName" : "lastInitial";

      if (!byIndex[idx]) byIndex[idx] = { firstName: "", lastInitial: "" };
      byIndex[idx][field] = String(value || "").trim();
    }

    const students = Object.values(byIndex).filter((s) => s.firstName);
    return students;
  };

  useEffect(() => {
    if (!results.length || !selectedTemplateId) return;
  
    const loadSentStatus = async () => {
      const q = query(
        collection(db, "emailLogs"),
        where("templateId", "==", selectedTemplateId)
      );
  
      const snap = await getDocs(q);
  
      const map = {};
      snap.forEach((doc) => {
        const { to } = doc.data();
        map[to] = true;
      });
  
      setSentMap(map);
    };
  
    loadSentStatus();
  }, [db, results, selectedTemplateId]);  

  const handleProcess = async () => {
    if (!rows.length) {
      alert("Please upload a file with at least one row.");
      return;
    }

    setIsProcessing(true);
    setError("");
    setResults([]);
    setProgress(0);

    const auth = getAuth();
    const adminUid = auth.currentUser?.uid || null;
    if (!adminUid) {
      setIsProcessing(false);
      setError("No admin user signed in. Please log in and try again.");
      return;
    }

    const outResults = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const educator = mapRowToEducator(row);
        const students = extractStudentsFromRow(row);

        const res = await createEducatorWithStudents({
          db,
          adminUid,
          educator,
          students,
        });

        outResults.push({
          rowIndex: i + 1,
          ...res,
        });
      } catch (err) {
        console.error("Row failed:", i + 1, err);
        outResults.push({
          rowIndex: i + 1,
          error: err?.message || String(err),
        });
      }

      setProgress(Math.round(((i + 1) / rows.length) * 100));
    }

    setResults(outResults);
    setIsProcessing(false);
  };

  // ---- CSV DOWNLOAD ----
const downloadCSV = () => {
  if (!results.length) {
    alert("No data to export.");
    return;
  }

  const lines = [];

  // Header
  lines.push([
    "Row",
    "Educator Name",
    "Educator Email",
    "Educator Password",
    "School Name",
    "Educator Code",
    "Student Number",
    "Student Name",
    "Student Email",
    "Student Password"
  ].join(","));

  results.forEach((r) => {
    if (r.error) {
      lines.push([`Row ${r.rowIndex}`, `ERROR: ${r.error}`]);
      lines.push("");
      return;
    }
  
    const { educator, students } = r;
    const eduName = `${educator.sirName} ${educator.lastName}`.trim();
  
    // Educator first row
    lines.push([
      r.rowIndex,
      eduName,
      educator.email,
      educator.password,
      educator.schoolName,
      educator.educatorCode,
      "",
      "",
      "",
      ""
    ].join(","));
  
    // Student rows
    students.forEach((s) => {
      lines.push([
        "",
        "",
        "",
        "",
        "",
        "",
        s.studentNumber,
        s.name,
        s.email,
        s.password
      ].join(","));
    });
  
    // Separator
    lines.push("");
  });
  

  const csvContent = lines.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `bulk-upload-results.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generateEmailForEducator = (educator, students = []) => {
  if (!selectedTemplate) return null;

  const studentsBlock = buildStudentsBlock(students);

  const data = {
    educatorName: `${educator.sirName} ${educator.lastName}`.trim(),
    username: educator.email,
    password: educator.password,
    schoolName: educator.schoolName,
    trialEndDate: "March 1",
    studentsBlock,
  };

  return {
    subject: selectedTemplate.subject,
    body: mergeTemplate(selectedTemplate.body, data),
  };
};

const buildStudentsBlock = (students = []) => {
  if (!students.length) return "";

  let block = "Student Login Details:\n";

  students.forEach((s) => {
    block += `• ${s.studentNumber}. ${s.name}\n`;
    block += `  Username: ${s.email}\n`;
    block += `  Password: ${s.password}\n\n`;
  });

  return block.trim();
};

const sendAllEmails = async () => {
  const pending = results.filter(
    (r) => !r.error && !sentMap[r.educator.email]
  );

  if (!pending.length) {
    alert("No pending emails to send.");
    return;
  }

  if (!window.confirm(`Send ${pending.length} emails now?`)) return;

  setSendingBulk(true);
  setBulkProgress(0);

  let sentCount = 0;

  for (const r of pending) {
    const email = generateEmailForEducator(r.educator, r.students);
    if (!email) continue;

    try {
      await sendEmailViaGmail({
        to: r.educator.email,
        subject: email.subject,
        body: email.body,
        templateId: selectedTemplateId,
      });

      sentCount++;

      setSentMap((prev) => ({
        ...prev,
        [r.educator.email]: true,
      }));

      setBulkProgress(
        Math.round((sentCount / pending.length) * 100)
      );
    } catch (err) {
      console.error("Bulk send failed:", r.educator.email, err);
    }
  }

  setSendingBulk(false);
  alert("Bulk send completed.");
};

const EmailPreviewModal = ({ email, onClose }) => {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(
      `Subject: ${email.subject}\n\n${email.body}`
    );
    alert("Email copied to clipboard");
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Email Preview</h3>

        <strong>Subject</strong>
        <p>{email.subject}</p>

        <strong>Body</strong>
        <pre style={{ whiteSpace: "pre-wrap" }}>{email.body}</pre>

        <div style={{ marginTop: 12 }}>
          <button className="blue-button" onClick={copyToClipboard}>
            Copy Email
          </button>
          <button
            style={{ marginLeft: 10 }}
            className="gray-button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="container">
      <button onClick={() => navigate("/admin")} className="return-button">
        Return to Admin Dashboard
      </button>

      <h1>Bulk Upload Educators & Students</h1>

      <div className="section">
        <p>
          Upload an Excel file with educator and student data. Each row should represent one educator,
          with their students listed as <code>Student1 First Name</code>, <code>Student1 Last Initial</code>, etc.
        </p>

        <div style={{ marginBottom: 20 }}>
    <h3>Download Bulk Upload Template</h3>
    <p>
      Use this Excel template to prepare educator and student data for bulk upload.
      Do not rename column headers.
    </p>

    <a
      href="/templates/AdminBulkUploadTemplate.xlsx"
      download
      className="blue-button"
      style={{ display: "inline-block" }}
    >
      Download Excel Template
    </a>
  </div>
  
        <div className="upload-area">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
        </div>

        {rows.length > 0 && (
          <p>
            <strong>{rows.length}</strong> rows loaded from file.
          </p>
        )}
<div style={{ marginBottom: 16 }}>
  <label style={{ fontWeight: "bold" }}>
    Email Template
  </label>
  <br />

  {loadingTemplates ? (
    <div>Loading templates…</div>
  ) : (
    <select
      value={selectedTemplateId}
      onChange={(e) => setSelectedTemplateId(e.target.value)}
      style={{ padding: 6, minWidth: 260, marginTop: 6 }}
      disabled={!emailTemplates.length}
    >
      {emailTemplates.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  )}
</div>

        <button
          onClick={handleProcess}
          disabled={!rows.length || isProcessing}
          className="add-button"
        >
          {isProcessing ? "Processing..." : "Create Accounts"}
        </button>

        {isProcessing && (
          <div style={{ marginTop: 10 }}>
            <div>Progress: {progress}%</div>
            <div className="progress-bar">
              <div
                className="progress-bar-inner"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="error-box" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="section">
          <h2>Created Accounts (Summary)</h2>
          <p>
  Below is a grouped list for each uploaded educator, including their school and all
  students created under them.
</p>

<button
  className="blue-button"
  style={{ marginBottom: 12 }}
  onClick={sendAllEmails}
>
  Send All Pending Emails
</button>

{sendingBulk && (
  <div style={{ marginBottom: 12 }}>
    <strong>Sending emails:</strong> {bulkProgress}%
    <div className="progress-bar">
      <div
        className="progress-bar-inner"
        style={{ width: `${bulkProgress}%` }}
      />
    </div>
  </div>
)}

          <div className="bordered-container" style={{ maxHeight: 500, overflowY: "auto" }}>
            {results.map((r, idx) => {
              if (r.error) {
                return (
                  <div key={idx} className="result-block error">
                    <strong>Row {r.rowIndex}:</strong> {r.error}
                  </div>
                );
              }

              const { educator, students } = r;
              return (
                <div
                  key={idx}
                  className="result-block"
                  style={{
                    padding: "10px 0",
                    borderBottom: "1px solid #ddd",
                    marginBottom: 8, // visual "blank row" between educators
                  }}
                >
                  <div><strong>Row:</strong> {r.rowIndex}</div>
                  <div><strong>Educator Name:</strong> {`${educator.sirName} ${educator.lastName}`}</div>
                  <div><strong>School:</strong> {educator.schoolName}</div>
                  <div><strong>Username:</strong> {educator.email}</div>
                  <div><strong>Password:</strong> {educator.password}</div>
                  <div><strong>Password:</strong> {educator.password}</div>

{sentMap[educator.email] ? (
  <span className="badge badge-sent">Email Sent</span>
) : (
  <span className="badge badge-pending">Not Sent</span>
)}

                  <button
  className="blue-button"
  style={{ marginTop: 8 }}
  disabled={sentMap[educator.email]}
  onClick={() =>
    setEmailPreview(
      generateEmailForEducator(educator, students)
    )
  }
>
  {sentMap[educator.email] ? "Email Already Sent" : "Generate Email"}
</button>

                  {educator.educatorCode && (
                    <div><strong>Educator Code:</strong> {educator.educatorCode}</div>
                  )}

                  {students && students.length > 0 && (
                    <>
                      <div style={{ marginTop: 8 }}>
                        <strong>Students:</strong>
                      </div>
                      <table className="excel-table" style={{ marginTop: 4 }}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Username</th>
                            <th>Password</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((s, i2) => (
                            <tr key={i2}>
                              <td>{s.studentNumber}</td>
                              <td>{s.name}</td>
                              <td>{s.email}</td>
                              <td>{s.password}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {results.length > 0 && (
  <button
    onClick={downloadCSV}
    className="blue-button"
    style={{ marginTop: 20, marginBottom: 10 }}
  >
    Download CSV Summary
  </button>
)}

                    </>
                  )}
                  {emailPreview && (
  <EmailPreviewModal
    email={emailPreview}
    onClose={() => setEmailPreview(null)}
  />
)}

                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBulkUpload;
